import { envConfig } from '../env';

/** Empty in dev (Vite proxies `/api`). In production must be full origin, e.g. `https://your-api.vercel.app` — no trailing slash. */
function normalizeApiBase(raw: string): string {
  return raw.trim().replace(/\/+$/, '');
}

const base = normalizeApiBase(envConfig.apiBaseUrl ?? '');

function joinApiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/** Absolute URL when helpful for logs/errors (same-origin relative paths resolve against current tab). */
function describeResolvedApiUrl(url: string): string {
  if (url.startsWith('http')) return url;
  if (typeof window !== 'undefined') {
    try {
      return new URL(url, window.location.origin).href;
    } catch {
      return url;
    }
  }
  return url;
}

function describePayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const o = payload as Record<string, unknown>;
  if (typeof o.error === 'string') return o.error;
  if (typeof o.message === 'string') return o.message;
  const d = o.detail;
  if (typeof d === 'string') return d;
  return null;
}

/** Central API wrapper — attaches Supabase Bearer token for Nest/Express backends. */
export async function apiFetch<T>(
  path: string,
  opts: RequestInit & { token?: string } = {}
): Promise<T> {
  const url = joinApiUrl(path);

  if (import.meta.env.PROD && !base && path.startsWith('/api')) {
      console.warn(
        '[bcc] VITE_API_BASE_URL is empty — requests use same-origin /api. On Cloudflare Pages, deploy functions/api/[[path]].js and set API_BASE_URL; otherwise set VITE_API_BASE_URL to your API origin.'
      );
    }

  const headers = new Headers(opts.headers);
  headers.set('Content-Type', 'application/json');
  if (opts.token) headers.set('Authorization', `Bearer ${opts.token}`);

  let res: Response;
  try {
    res = await fetch(url, { ...opts, headers });
  } catch (e) {
    const resolved = describeResolvedApiUrl(url);
    const hint =
      import.meta.env.PROD && !base
        ? ` Likely cause: VITE_API_BASE_URL / API_BASE_URL was not set when this SPA was built — requests resolve to ${resolved}.`
        : '';
    if (e instanceof TypeError) {
      throw new Error(`Could not reach API at ${resolved}.${hint} (${e.message})`);
    }
    throw e;
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = { detail: await res.text() };
    }
    const fromServer = describePayload(body);
    let message: string;
    if (fromServer) message = fromServer;
    else if (res.status === 404 && !base && import.meta.env.PROD) {
      message =
        'No API on this host (404). Add VITE_API_BASE_URL to your production build env (full URL of your Express/Vercel API) and redeploy the frontend.';
    } else if (res.status === 404) {
      message = 'Not found.';
    } else {
      message = `Request failed (${res.status})`;
    }
    throw new ApiError(res.status, body, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Multipart POST — do not set Content-Type (browser sets boundary). */
export async function apiPostMultipart<T>(
  path: string,
  formData: FormData,
  opts: { token?: string } = {}
): Promise<T> {
  const url = joinApiUrl(path);
  const headers = new Headers();
  if (opts.token) headers.set('Authorization', `Bearer ${opts.token}`);

  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', body: formData, headers });
  } catch (e) {
    const resolved = describeResolvedApiUrl(url);
    if (e instanceof TypeError) {
      throw new Error(`Could not reach API at ${resolved}. (${e.message})`);
    }
    throw e;
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = { detail: await res.text() };
    }
    const fromServer = describePayload(body);
    throw new ApiError(res.status, body, fromServer ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
    message?: string
  ) {
    super(message ?? describePayload(payload) ?? `API Error (${status})`);
    this.name = 'ApiError';
  }
}
